import { useEffect, useState } from "react";
import { Link, useLocation } from "react-router-dom";
import { AnimatePresence, motion } from "framer-motion";
import { NAV } from "../lib/site";
import Logo from "./Logo";

const Navbar = () => {
    const [scrolled, setScrolled] = useState(false);
    const [open, setOpen] = useState(false);
    const { pathname } = useLocation();
    const darkTop = pathname === "/business-x-ray" && !scrolled;

    useEffect(() => {
        const onScroll = () => setScrolled(window.scrollY > 24);
        onScroll();
        window.addEventListener("scroll", onScroll, { passive: true });
        return () => window.removeEventListener("scroll", onScroll);
    }, []);

    useEffect(() => setOpen(false), [pathname]);

    useEffect(() => {
        document.body.style.overflow = open ? "hidden" : "";
        return () => (document.body.style.overflow = "");
    }, [open]);

    return (
        <>
            <header className={`fixed inset-x-0 top-0 z-50 transition-all duration-500 ${scrolled ? "border-b border-ink/10 bg-ivory/85 backdrop-blur-xl" : "border-b border-transparent bg-transparent"}`}>
                <div className="container-x flex h-[72px] items-center justify-between gap-6">
                    <Link to="/" data-testid="nav-logo-link" aria-label="Vantera home">
                        <Logo size={26} dark={darkTop} />
                    </Link>
                    <nav className="hidden items-center gap-6 xl:flex" aria-label="Primary">
                        {NAV.map((n) => (
                            <Link
                                key={n.path}
                                to={n.path}
                                data-testid={`nav-link-${n.path === "/" ? "home" : n.path.slice(1)}`}
                                className={`group relative py-2 text-[13.5px] font-semibold tracking-tight transition-colors duration-300 ${pathname === n.path ? (darkTop ? "text-ivory" : "text-ink") : darkTop ? "text-cloud hover:text-ivory" : "text-slategray hover:text-ink"}`}
                            >
                                {n.label}
                                <span className={`absolute -bottom-0.5 left-0 h-[2px] w-full origin-left bg-vantera transition-transform duration-300 ${pathname === n.path ? "scale-x-100" : "scale-x-0 group-hover:scale-x-100"}`} />
                            </Link>
                        ))}
                    </nav>
                    <div className="flex items-center gap-3">
                        <Link
                            to="/contact"
                            data-testid="nav-cta-request-xray"
                            className={`hidden items-center gap-2 rounded-full px-5 py-2.5 font-display text-[13.5px] font-bold transition-colors duration-300 md:inline-flex ${darkTop ? "bg-vantera text-ink hover:bg-ivory" : "bg-ink text-ivory hover:bg-vantera hover:text-ink"}`}
                        >
                            Request a Business X-Ray <span aria-hidden="true">→</span>
                        </Link>
                        <button
                            onClick={() => setOpen(!open)}
                            data-testid="nav-mobile-toggle"
                            aria-expanded={open}
                            aria-label={open ? "Close menu" : "Open menu"}
                            className={`flex size-11 flex-col items-center justify-center gap-[5px] rounded-full border transition-colors duration-300 xl:hidden ${darkTop ? "border-white/25" : "border-ink/15"}`}
                        >
                            <span className={`h-[1.5px] w-4 transition-transform duration-300 ${darkTop ? "bg-ivory" : "bg-ink"} ${open ? "translate-y-[6.5px] rotate-45" : ""}`} />
                            <span className={`h-[1.5px] w-4 transition-opacity duration-300 ${darkTop ? "bg-ivory" : "bg-ink"} ${open ? "opacity-0" : ""}`} />
                            <span className={`h-[1.5px] w-4 transition-transform duration-300 ${darkTop ? "bg-ivory" : "bg-ink"} ${open ? "-translate-y-[6.5px] -rotate-45" : ""}`} />
                        </button>
                    </div>
                </div>
            </header>

            <AnimatePresence>
                {open && (
                    <motion.div
                        initial={{ opacity: 0 }}
                        animate={{ opacity: 1 }}
                        exit={{ opacity: 0 }}
                        transition={{ duration: 0.3 }}
                        className="fixed inset-0 z-40 flex flex-col bg-ink bg-grid-dark"
                        data-testid="nav-mobile-menu"
                    >
                        <div className="flex-1 overflow-y-auto px-6 pt-28 pb-10">
                            <nav aria-label="Mobile" className="flex flex-col">
                                {NAV.map((n, i) => (
                                    <motion.div key={n.path} initial={{ opacity: 0, x: -18 }} animate={{ opacity: 1, x: 0 }} transition={{ delay: 0.06 + i * 0.045, duration: 0.45, ease: [0.16, 1, 0.3, 1] }}>
                                        <Link
                                            to={n.path}
                                            data-testid={`nav-mobile-link-${n.path === "/" ? "home" : n.path.slice(1)}`}
                                            className={`group flex items-baseline justify-between border-b border-white/10 py-4 font-display text-[30px] font-extrabold tracking-tightest ${pathname === n.path ? "text-vantera" : "text-ivory"}`}
                                        >
                                            {n.label}
                                            <span className="font-mono text-[11px] text-cloud">0{i + 1}</span>
                                        </Link>
                                    </motion.div>
                                ))}
                            </nav>
                            <motion.div initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.5 }} className="mt-10">
                                <Link to="/contact" data-testid="nav-mobile-cta" className="flex w-full items-center justify-center gap-2 rounded-full bg-vantera py-4 font-display text-base font-bold text-ink">
                                    Request a Business X-Ray <span aria-hidden="true">→</span>
                                </Link>
                                <p className="mt-6 text-center font-mono text-[10px] uppercase tracking-[0.3em] text-cloud">See the business differently</p>
                            </motion.div>
                        </div>
                    </motion.div>
                )}
            </AnimatePresence>
        </>
    );
};

export default Navbar;